
import React from 'react';
import { Button } from "../components/ui/button";
import { ArrowRight, Download } from 'lucide-react';
import { Car, Tractor, Users, BarChart3, Truck, Calendar, MapPin } from 'lucide-react';

// Componentes
import HeroSection from '../components/sections/HeroSection';
import StatsSection from '../components/sections/StatsSection';
import TimelineSection from '../components/sections/TimelineSection';
import InvestmentChart from '../components/charts/InvestmentChart';
import VehicleProductionChart from '../components/charts/VehicleProductionChart';
import PassengerChart from '../components/charts/PassengerChart';
import AccidentChart from '../components/charts/AccidentChart';

import rodoviario_PAV from "../assets/img/mapa/rodoviario_PAV.png";

import diagnosticoPdf from "../assets/diagnostico/diagnostivo2024.pdf";



// Dados da publicação RODOVIÁRIA
const roadwayData = {
  title: "Diagnóstico Logístico Rodoviário",
  subtitle: "Análise e marcos do transporte rodoviário no Brasil em 2024",
  year: "2024",
  
  // Estatísticas principais
  stats: [
    { 
      label: "Investimento Público", 
      value: 13.4, 
      suffix: "Bilhões", 
      prefix: "R$",
      description: "Crescimento de 5,2% em relação a 2023",
      color: "text-blue-600"
    },
    { 
      label: "Malha Concedida", 
      value: 14.1, 
      suffix: "mil km",
      description: "Aumento de 1,07 mil km em relação a 2023",
      color: "text-green-600"
    },
    { 
      label: "Produção de Veículos", 
      value: 2.56, 
      suffix: "Milhões",
      description: "Retomada da oitava posição no ranking mundial",
      color: "text-purple-600"
    },
    { 
      label: "Frota Total", 
      value: 124, 
      suffix: "Milhões",
      description: "Aumento de 5,1% ao ano desde 2010",
      color: "text-orange-600"
    }
  ],

  // Marcos mensais do transporte rodoviário
  timeline: [
    { month: "Janeiro", title: "Novos contratos de concessão no Paraná", description: "Via Araucária e EPR Litoral Pioneiro assumem 1.076 km de rodovias, marcando a 5ª Etapa do PROCROFE.", icon: "✍️" },
    { month: "Fevereiro", title: "Novo Marco Regulatório para Passageiros", description: "Resolução ANTT nº 6.033/2023 entra em vigor para promover maior concorrência e segurança no transporte interestadual.", icon: "📜" },
    { month: "Março", title: "Modernização do Transporte de Cargas (TRIC)", description: "Portaria ANTT nº 6 traz mais clareza e segurança jurídica para o transporte rodoviário internacional de cargas.", icon: "🚚" },
    { month: "Abril", title: "Leilão da BR-040/MG (Belo Horizonte - Juiz de Fora)", description: "Concessão de 232 km com investimentos de R$ 9 bilhões, arrematada pelo Consórcio Infraestrutura MG.", icon: "🏛️" },
    { month: "Maio", title: "Instituído o Planejamento Integrado de Transportes (PIT)", description: "Decreto nº 12.022 visa criar um planejamento integrado da rede de transporte a partir de uma visão territorial.", icon: "🗺️" },
    { month: "Junho", title: "Operação São João 2024", description: "Polícia Rodoviária Federal reforça fiscalização em rodovias baianas contra infrações e acidentes graves.", icon: "🚦" },
    { month: "Julho", title: "Publicação de manuais de custos pelo DNIT", description: "Cinco novos manuais para padronizar critérios em projetos e estudos, promovendo maior transparência.", icon: "📚" },
    { month: "Agosto", title: "Ampliação da Serra das Araras na Via Dutra", description: "Detonação de rochas para construção de novas pistas, um dos maiores projetos de ampliação rodoviária do país.", icon: "⛰️" },
    { month: "Setembro", title: "Evento da ANTT sobre Sustentabilidade e Tecnologia", description: "Foco na integração entre inovação, eficiência energética e os ODS, com lançamento do Boletim Via Sustentável.", icon: "🌱" },
    { month: "Outubro", title: "Regulamentação de Pontos de Parada e Descanso (PPDs)", description: "Resolução nº 6.054 aprimora as condições de trabalho e segurança para motoristas profissionais.", icon: "🛌" },
    { month: "Novembro", title: "Programa de Otimização de Contratos de Concessão", description: "Previsão de R$ 110 bilhões em investimentos para duplicar e modernizar a malha viária existente.", icon: "💰" },
    { month: "Dezembro", title: "Recorde de leilões rodoviários", description: "Ciclo de 9 leilões entre 2023-2024 garante R$ 111 bilhões em investimentos e 4.300 km concedidos.", icon: "🏆" }
  ]
};

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <HeroSection data={roadwayData} />
      
      <StatsSection stats={roadwayData.stats} />
      
      <TimelineSection timeline={roadwayData.timeline} />
      
      {/* Seção de Investimentos */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Evolução dos Investimentos</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Análise da evolução dos investimentos públicos no setor rodoviário brasileiro
            </p>
          </div>
          <InvestmentChart />
        </div>
      </section>

      {/* Seção de Produção de Veículos */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Produção de Veículos</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Retomada da produção de comerciais leves e automóveis no Brasil
            </p>
          </div>
          <VehicleProductionChart />
        </div>
      </section>

      {/* Seção de Transporte de Passageiros */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Transporte de Passageiros</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dinâmica da oferta de assentos e demanda no transporte regular interestadual
            </p>
          </div>
          <PassengerChart />
        </div>
      </section>

      {/* Seção de Sinistros */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <AccidentChart />
        </div>
      </section>

      {/* Seção Infraestrutura */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Infraestrutura Rodoviária</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Mapa da malha rodoviária federal pavimentada e não pavimentada no Brasil
            </p>
          </div>

   
          <div className="flex justify-center mb-16">
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet={rodoviario_PAV}
              />
              <img
                src={rodoviario_PAV}
                alt="Mapa da malha rodoviária federal pavimentada e não pavimentada no Brasil"
                className="rounded-lg shadow-xl w-full max-w-6xl h-auto border-4 border-white"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>

          <div className="mt-16 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Condição da Malha Rodoviária Federal</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="text-4xl font-bold text-green-600 mb-2">74,9%</div>
                  <p className="text-gray-600">das rodovias classificadas como boas ou regulares em 2024</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-blue-600 mb-2">96%</div>
                  <p className="text-gray-600">da extensão coberta por contratos ativos de manutenção</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Acesse o Diagnóstico Completo
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Baixe o documento completo e tenha acesso a todos os dados, análises detalhadas e 
            insights estratégicos sobre o setor rodoviário brasileiro.
          </p>
    <a
  href={diagnosticoPdf}
  download="diagnostico2024.pdf"
  rel="noopener noreferrer"
>
  <Button
    size="lg"
    className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 text-lg group"
  >
    <Download className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
    Download do Diagnóstico
    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
  </Button>
</a>
        </div>
      </section>

      {/* Footer */}
      {/* <footer className="py-12 bg-[#193e73] text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Truck className="w-6 h-6" />
                INFRA S.A.
              </h3>
              <p className="text-gray-400 mb-4">
                Observatório Nacional de Transporte e Logística
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <MapPin className="w-4 h-4" />
                Brasília, DF - Brasil
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Recursos</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Diagnósticos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Relatórios</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Dados Abertos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Metodologia</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Fale Conosco</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Imprensa</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Parcerias</a></li>
                <li><a href="https://www.infrasa.gov.br" className="hover:text-white transition-colors">Site Oficial</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 INFRA S.A. - Todos os direitos reservados.</p>
          </div>
        </div>
      </footer> */}
    </div>
  );
}